/**
 * Discoverability Client Helpers
 *
 * Fire-and-forget feature adoption marks from surfaces outside the React hook.
 *
 * Primary responsibilities:
 * - POST usage marks without requiring DiscoverabilityProvider context
 * - Keep product-event call sites from inventing a second tracking path
 *
 * Dependencies:
 * - /api/discoverability/usage
 */

'use client'

import type { DiscoverabilityTrackedFeatureKey } from './features'

const SEARCH_MARK_SESSION_KEY = 'losi.discoverability.searchMarked'

/** Search marks once per tab session — debounced keystrokes must not spam `/usage`. */
function shouldSkipSearchMark(featureKey: DiscoverabilityTrackedFeatureKey): boolean {
  if (featureKey !== 'search') return false
  try {
    if (sessionStorage.getItem(SEARCH_MARK_SESSION_KEY) === '1') return true
    sessionStorage.setItem(SEARCH_MARK_SESSION_KEY, '1')
  } catch {
    // sessionStorage may be blocked; fall through and mark once via network.
  }
  return false
}

export async function clientMarkFeatureUsed(input: {
  featureKey: DiscoverabilityTrackedFeatureKey
  surface?: string | null
  trigger?: string | null
  metadata?: unknown
}): Promise<void> {
  if (typeof window === 'undefined') return
  if (shouldSkipSearchMark(input.featureKey)) return

  try {
    await fetch('/api/discoverability/usage', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    })
  } catch {
    // Adoption tracking must never block product UX.
  }
}
